import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Grid, Button, Chip, Accordion, AccordionSummary,
  AccordionDetails, CircularProgress, Alert, FormControl, InputLabel,
  Select, MenuItem, Stack, Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import { format } from 'date-fns';
import { API_BASE_URL } from '../services/api';

const ALL_MONTHS = [
  { num: 1, name: 'January' }, { num: 2, name: 'February' }, { num: 3, name: 'March' },
  { num: 4, name: 'April' }, { num: 5, name: 'May' }, { num: 6, name: 'June' },
  { num: 7, name: 'July' }, { num: 8, name: 'August' }, { num: 9, name: 'September' },
  { num: 10, name: 'October' }, { num: 11, name: 'November' }, { num: 12, name: 'December' },
];

const CATEGORY_COLORS = {
  gsec_buy:      '#2563eb',
  gsec_sell:     '#7c3aed',
  buyback_buy:   '#0d9488',
  buyback_sell:  '#9333ea',
  repo:          '#0ea5e9',
  reverse_repo:  '#0891b2',
  mm_lending:    '#16a34a',
  mm_borrowing:  '#ca8a04',
  fixed_deposit: '#db2777',
  maturity:      '#f97316',
  other:         '#6b7280',
};

const formatMoney = (value, currency = 'LKR') => {
  const n = parseFloat(value) || 0;
  return new Intl.NumberFormat('en-LK', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const formatNumber = (value) => {
  const n = parseFloat(value) || 0;
  return new Intl.NumberFormat('en-LK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const csvEscape = (val) => {
  const s = val == null ? '' : String(val);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCsvForMonth = (bucket) => {
  const lines = [];
  lines.push([
    'Value Date', 'Deal Number', 'Account Code', 'Account Name', 'Category',
    'Description', 'Debit', 'Credit', 'Posted', 'Estimated',
  ].join(','));
  Object.values(bucket.groups).forEach((group) => {
    group.entries.forEach((entry) => {
      const dr = entry.posted ? (entry.debit_amount  || 0) : (entry.estimated_debit  || 0);
      const cr = entry.posted ? (entry.credit_amount || 0) : (entry.estimated_credit || 0);
      lines.push([
        entry.transaction_date ? format(new Date(entry.transaction_date), 'yyyy-MM-dd') : '',
        entry.deal_number || '',
        entry.account_code || '',
        entry.account_name || '',
        group.label,
        entry.ledger_description || (entry.posted ? '' : `Estimated: ${entry.deal_status}, ledger not yet posted`),
        dr,
        cr,
        entry.posted ? 'Yes' : 'No',
        entry.posted ? '' : 'Estimated',
      ].map(csvEscape).join(','));
    });
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `settlement-preview-${bucket.label.replace(/\s+/g, '-').toLowerCase()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ----------------- Single month panel -----------------
const MonthPanel = ({ bucket, onExport }) => {
  const { totals, groups, label } = bucket;
  const net = (totals.debit || 0) - (totals.credit || 0);
  const nonEmptyGroups = Object.entries(groups).filter(([, g]) => g.entries.length > 0);
  const unpostedTotal = totals.unposted || 0;

  return (
    <Paper
      elevation={2}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid #e0e7ff',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          color: 'white',
          px: 2.5, py: 1.5,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>{label}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {totals.count} posted lines · {nonEmptyGroups.length} {nonEmptyGroups.length === 1 ? 'category' : 'categories'}
              </Typography>
              {unpostedTotal > 0 && (
                <Chip
                  label={`${unpostedTotal} unposted deals`}
                  size="small"
                  sx={{ bgcolor: '#fbbf24', color: '#1f2937', fontSize: '0.68rem', height: 18, fontWeight: 700 }}
                />
              )}
            </Stack>
          </Box>
          <Button
            type="button"
            onClick={() => { if (typeof onExport === 'function') onExport(bucket); }}
            disabled={totals.count === 0 && unpostedTotal === 0}
            size="small"
            startIcon={<DownloadIcon fontSize="small" />}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.15)',
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.05)' },
            }}
          >
            CSV
          </Button>
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Posted Debit</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{formatNumber(totals.debit)}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Posted Credit</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{formatNumber(totals.credit)}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Net</Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: Math.abs(net) < 0.01 ? 'rgba(255,255,255,0.95)' : (net > 0 ? '#bbf7d0' : '#fecaca'),
              }}
            >
              {formatNumber(net)}
            </Typography>
          </Box>
          {(totals.estimatedDebit > 0 || totals.estimatedCredit > 0) && (
            <>
              <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.8, color: '#fde68a' }}>
                  Estimated Dr / Cr (unposted)
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontStyle: 'italic', color: '#fde68a' }}>
                  {formatNumber(totals.estimatedDebit)} / {formatNumber(totals.estimatedCredit)}
                </Typography>
              </Box>
            </>
          )}
        </Stack>
      </Box>

      {/* Groups */}
      <Box sx={{ p: 1.5, bgcolor: '#fafbff', flex: 1 }}>
        {nonEmptyGroups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <Typography variant="body2">No transactions in {label}</Typography>
            <Typography variant="caption">Daily accrual / amortization entries are excluded.</Typography>
          </Box>
        ) : (
          nonEmptyGroups.map(([key, group]) => {
            const postedEntries   = group.entries.filter((e) => e.posted);
            const unpostedEntries = group.entries.filter((e) => !e.posted);
            return (
              <Accordion
                key={key}
                disableGutters
                defaultExpanded={group.entries.length <= 12}
                sx={{
                  mb: 1,
                  borderLeft: `4px solid ${CATEGORY_COLORS[key] || '#6b7280'}`,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  '&:before': { display: 'none' },
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                    <Chip
                      label={group.label}
                      size="small"
                      sx={{ bgcolor: CATEGORY_COLORS[key] || '#6b7280', color: 'white', fontWeight: 600, fontSize: '0.72rem' }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {postedEntries.length} posted
                    </Typography>
                    {unpostedEntries.length > 0 && (
                      <Chip
                        label={`${unpostedEntries.length} unposted`}
                        size="small"
                        sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: '0.68rem', height: 18, fontWeight: 600 }}
                      />
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ color: '#374151' }}>
                      Dr {formatNumber(group.totals.debit)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#374151' }}>
                      Cr {formatNumber(group.totals.credit)}
                    </Typography>
                    {(group.totals.estimatedDebit > 0 || group.totals.estimatedCredit > 0) && (
                      <Typography variant="caption" sx={{ color: '#92400e', fontStyle: 'italic' }}>
                        + est. {formatNumber(group.totals.estimatedDebit)}
                      </Typography>
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, bgcolor: 'white' }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem' }}>Value Date</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem' }}>Deal</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem' }}>Account</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem' }}>Description</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>Debit</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>Credit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.entries.map((entry, idx) => {
                          const unposted = !entry.posted;
                          const drVal = unposted
                            ? (parseFloat(entry.estimated_debit) || 0)
                            : (parseFloat(entry.debit_amount) || 0);
                          const crVal = unposted
                            ? (parseFloat(entry.estimated_credit) || 0)
                            : (parseFloat(entry.credit_amount) || 0);
                          const rowKey = entry.ledger_id != null
                            ? `le-${entry.ledger_id}`
                            : `est-${entry.deal_number}-${entry.line_role || 'x'}-${idx}`;
                          return (
                            <TableRow
                              key={rowKey}
                              hover
                              sx={unposted ? { bgcolor: '#fffbeb' } : {}}
                            >
                              <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                                {entry.transaction_date
                                  ? format(new Date(entry.transaction_date), 'yyyy-MM-dd')
                                  : '-'}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                                <Stack direction="column" spacing={0.2}>
                                  <span>{entry.deal_number || '-'}</span>
                                  {entry.buyback_deal_number && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                                      BB: {entry.buyback_deal_number}
                                    </Typography>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.78rem' }}>
                                {entry.account_code ? (
                                  <Stack spacing={0.1}>
                                    <span
                                      title={entry.account_name || ''}
                                      style={unposted ? { fontStyle: 'italic', color: '#92400e' } : undefined}
                                    >
                                      {entry.account_code}
                                    </span>
                                    {entry.account_name && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: unposted ? '#a16207' : 'text.secondary',
                                          fontSize: '0.66rem',
                                          lineHeight: 1.1,
                                          maxWidth: 180,
                                          display: 'block',
                                        }}
                                      >
                                        {entry.account_name}
                                      </Typography>
                                    )}
                                  </Stack>
                                ) : '-'}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.78rem', maxWidth: 320 }}>
                                <Stack spacing={0.1}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'block',
                                      lineHeight: 1.3,
                                      color: unposted ? '#92400e' : 'inherit',
                                      fontStyle: unposted ? 'italic' : 'normal',
                                    }}
                                  >
                                    {entry.ledger_description || '-'}
                                  </Typography>
                                  {unposted && (
                                    <Typography variant="caption" sx={{ color: '#a16207', fontSize: '0.66rem', fontWeight: 600 }}>
                                      (estimated · deal {entry.deal_status})
                                    </Typography>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontSize: '0.78rem',
                                  fontVariantNumeric: 'tabular-nums',
                                  fontStyle: unposted ? 'italic' : 'normal',
                                  color: unposted ? '#92400e' : 'inherit',
                                }}
                              >
                                {drVal > 0 ? formatMoney(drVal) : ''}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontSize: '0.78rem',
                                  fontVariantNumeric: 'tabular-nums',
                                  fontStyle: unposted ? 'italic' : 'normal',
                                  color: unposted ? '#92400e' : 'inherit',
                                }}
                              >
                                {crVal > 0 ? formatMoney(crVal) : ''}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow sx={{ bgcolor: '#f9fafb' }}>
                          <TableCell colSpan={4} sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
                            Subtotal (posted)
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                            {formatNumber(group.totals.debit)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                            {formatNumber(group.totals.credit)}
                          </TableCell>
                        </TableRow>
                        {(group.totals.estimatedDebit > 0 || group.totals.estimatedCredit > 0) && (
                          <TableRow sx={{ bgcolor: '#fffbeb' }}>
                            <TableCell colSpan={4} sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#92400e', fontStyle: 'italic' }}>
                              Estimated subtotal (unposted)
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums', color: '#92400e', fontStyle: 'italic' }}>
                              {formatNumber(group.totals.estimatedDebit)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums', color: '#92400e', fontStyle: 'italic' }}>
                              {formatNumber(group.totals.estimatedCredit)}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Box>
    </Paper>
  );
};

// ----------------- Page -----------------
const SettlementPreviewPage = () => {
  const [year, setYear] = useState(2026);
  const [selectedMonths, setSelectedMonths] = useState([4, 5]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const start = Math.min(2024, current - 2);
    const end = Math.max(2028, current + 1);
    const arr = [];
    for (let y = start; y <= end; y++) arr.push(y);
    return arr;
  }, []);

  const fetchData = useCallback(async () => {
    if (selectedMonths.length === 0) {
      setError('Select at least one month');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      if (userData) headers['x-user-data'] = userData;

      const response = await axios.get(`${API_BASE_URL}/accounting/settlement-preview`, {
        headers,
        params: {
          year,
          months: selectedMonths.slice().sort((a, b) => a - b).join(','),
        },
      });
      setData(response.data);
    } catch (err) {
      console.error('Error loading settlement preview:', err);
      setError(err.response?.data?.error || 'Failed to load settlement preview. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [year, selectedMonths]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleMonth = (m) => {
    setSelectedMonths((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m].sort((a, b) => a - b)));
  };

  const months = data?.months || [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>
            Settlement Ledger Preview
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Transaction-driven view: G-Sec Buy/Sell, Buyback Buy/Sell, Repo/Reverse Repo — bucketed by value date, daily accruals excluded. Unposted deals highlighted.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel id="year-select-label">Year</InputLabel>
            <Select
              labelId="year-select-label"
              value={year}
              label="Year"
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }} elevation={0}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mr: 0.5 }}>
            MONTHS:
          </Typography>
          {ALL_MONTHS.map((m) => {
            const active = selectedMonths.includes(m.num);
            return (
              <Button
                key={m.num}
                size="small"
                onClick={() => toggleMonth(m.num)}
                variant={active ? 'contained' : 'outlined'}
                color={active ? 'primary' : 'inherit'}
                sx={{
                  minWidth: 64,
                  px: 1.2,
                  py: 0.2,
                  fontSize: '0.75rem',
                  fontWeight: active ? 600 : 500,
                  textTransform: 'none',
                  transition: 'transform 0.15s ease',
                  '&:hover': { transform: 'translateY(-1px)' },
                }}
              >
                {m.name}
              </Button>
            );
          })}
        </Stack>
      </Paper>

      {data?.excludedPatterns?.length > 0 && (
        <Alert
          severity="info"
          icon={<InfoOutlinedIcon />}
          sx={{ mb: 2, alignItems: 'center', '.MuiAlert-message': { width: '100%' } }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>EXCLUDED:</Typography>
            {data.excludedPatterns.map((p) => (
              <Chip
                key={p}
                label={p}
                size="small"
                sx={{ fontFamily: 'monospace', fontSize: '0.7rem', bgcolor: 'white' }}
              />
            ))}
          </Stack>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
          <CircularProgress />
        </Box>
      ) : months.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }} elevation={1}>
          <Typography variant="body1">No months selected.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2} alignItems="stretch">
          {months.map((bucket) => (
            <Grid
              size={{ xs: 12, md: months.length === 1 ? 12 : 6 }}
              key={bucket.month}
              sx={{ display: 'flex' }}
            >
              <Box sx={{ width: '100%' }}>
                <MonthPanel bucket={bucket} onExport={downloadCsvForMonth} />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SettlementPreviewPage;
